import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Pagination,
} from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { scheduleService } from '../../services/scheduleService';
import { useNotificationStore } from '../../store/notificationStore';
import type { ScheduleExecutionDto, ScheduleExecutionFilters } from '../../types/schedule';
import type { ExecutionStatus } from '../../types/schedule';
import { ExecutionStatuses } from '../../types/schedule';

interface ScheduleExecutionHistoryProps {
  scheduleId: string;
}

/**
 * Component to display schedule execution history.
 */
export const ScheduleExecutionHistory = ({ scheduleId }: ScheduleExecutionHistoryProps) => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  const [executions, setExecutions] = useState<ScheduleExecutionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState<ScheduleExecutionFilters>({
    pageNumber: 1,
    pageSize: 10,
  });

  useEffect(() => {
    loadExecutions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, filters.pageNumber, filters.status]);

  const loadExecutions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await scheduleService.getScheduleExecutions(scheduleId, filters);
      setExecutions(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err.message || 'Failed to load execution history');
      showNotification(err.message || 'Failed to load execution history', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: ExecutionStatus): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case ExecutionStatuses.Completed:
        return 'success';
      case ExecutionStatuses.Running:
        return 'info';
      case ExecutionStatuses.Failed:
        return 'error';
      case ExecutionStatuses.Pending:
        return 'warning';
      case ExecutionStatuses.Cancelled:
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Execution History
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Scheduled At</TableCell>
                  <TableCell>Started At</TableCell>
                  <TableCell>Completed At</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Retry Count</TableCell>
                  <TableCell>Error</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {executions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No executions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  executions.map((execution) => (
                    <TableRow key={execution.id} hover>
                      <TableCell>{formatDate(execution.scheduledAt)}</TableCell>
                      <TableCell>{formatDate(execution.startedAt)}</TableCell>
                      <TableCell>{formatDate(execution.completedAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={execution.status}
                          color={getStatusColor(execution.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{execution.retryCount}</TableCell>
                      <TableCell>
                        {execution.errorMessage ? (
                          <Tooltip title={execution.errorMessage}>
                            <Typography
                              variant="body2"
                              color="error"
                              sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {execution.errorMessage}
                            </Typography>
                          </Tooltip>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {execution.sessionId && (
                          <Tooltip title="View Session">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/sessions/${execution.sessionId}`)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalCount > 0 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.ceil(totalCount / (filters.pageSize || 10))}
                page={filters.pageNumber || 1}
                onChange={(_, page) => setFilters({ ...filters, pageNumber: page })}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};
